import React, { useMemo } from 'react';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import {
  DEFAULT_ALERT_THRESHOLD_PERCENT,
  USAGE_SEVERITY_COLORS,
  clampPercent,
  formatPercent,
  formatTokens,
  formatTokensCompact,
  getUsageSeverity,
} from '../../constants/aiUsage';

/**
 * One AI token meter — used for both the input and the output allowance.
 *
 * Presentation only: it takes a used/limit pair and derives its colour from the
 * shared severity rules, so the header popover, the Clients dialog and the JLL
 * overview all read identically.
 *
 * @param {object} props
 * @param {string} props.label meter caption, e.g. "Input tokens"
 * @param {number} [props.used] tokens consumed
 * @param {number} [props.limit] allowance
 * @param {number} [props.percent] uncapped percentage from the API; recomputed
 *   from used/limit when omitted
 * @param {number} [props.alertThresholdPercent] amber threshold for this client
 * @param {boolean} [props.compact] tighter type and bar, for the header popover
 */
const TokenUsageBar = ({
  label,
  used = 0,
  limit = 0,
  percent,
  alertThresholdPercent = DEFAULT_ALERT_THRESHOLD_PERCENT,
  compact = false,
}) => {
  // The API sends an uncapped percentage; recompute only if it was omitted.
  const usagePercent = useMemo(() => {
    if (percent !== null && percent !== undefined) return Number(percent) || 0;
    if (!limit) return 0;
    return (Number(used) || 0) / Number(limit) * 100;
  }, [percent, used, limit]);

  const severity = getUsageSeverity(usagePercent, alertThresholdPercent);
  const { main: barColor } = USAGE_SEVERITY_COLORS[severity];
  const overage = Math.max((Number(used) || 0) - (Number(limit) || 0), 0);

  const tooltip = overage > 0
    ? `${formatTokens(used)} of ${formatTokens(limit)} tokens — ${formatTokens(overage)} over the limit`
    : `${formatTokens(used)} of ${formatTokens(limit)} tokens`;

  return (
    <Box sx={{ width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          mb: 0.5,
        }}
      >
        <Typography
          variant={compact ? 'caption' : 'body2'}
          sx={{ fontWeight: 600, color: 'text.primary' }}
        >
          {label}
        </Typography>
        <Tooltip title={tooltip} arrow>
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', whiteSpace: 'nowrap', cursor: 'help' }}
          >
            {formatTokensCompact(used)} / {formatTokensCompact(limit)}
            <Box component="span" sx={{ color: barColor, fontWeight: 700, ml: 0.75 }}>
              {formatPercent(usagePercent)}
            </Box>
          </Typography>
        </Tooltip>
      </Box>

      <Tooltip title={tooltip} arrow>
        <LinearProgress
          variant="determinate"
          // Clamped for layout; the real figure is in the label and tooltip
          value={clampPercent(usagePercent)}
          aria-label={`${label}: ${formatPercent(usagePercent)} of the allowance used`}
          sx={{
            height: compact ? 6 : 8,
            borderRadius: 4,
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '& .MuiLinearProgress-bar': {
              backgroundColor: barColor,
              borderRadius: 4,
            },
          }}
        />
      </Tooltip>

      {overage > 0 && (
        <Typography variant="caption" sx={{ color: barColor, display: 'block', mt: 0.5 }}>
          {formatTokensCompact(overage)} tokens over the limit — billed as overage
        </Typography>
      )}
    </Box>
  );
};

export default TokenUsageBar;
